/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.applySearchParams();
      this.updateAvailability();

      this.setAttribute('loaded', '');

      if (this.variant) {
        this.handleVariantChange(new Event('change'));
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;
      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;
      const variantAvailable = this.variant && this.variant.available;
      const { is_backordered } = (this.variant && this.data.formatted[this.variant.id]) ?? false;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      if( is_backordered){
        this.addBtn.textContent = this.addBtn.dataset.backorderText;
      }else {
        this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
      }
      this.addBtn.disabled = !variantAvailable;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'off') return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === 'selection') {
        // If no variant is selected, show all first options as available
        if (!this.variant && this.selectedOptions.every(opt => opt === null)) {
          // For each selector, check which options are available as first selections
          this.optionSelectors.forEach((selector, selectorIndex) => {
            const options = selector.querySelectorAll('.js-option');
            options.forEach((optionEl) => {
              const optionValue = selector.dataset.selectorType === 'dropdown' ? optionEl.dataset.value : optionEl.value;
              // Check if any variant exists with this option value at this position
              const variantExists = this.data.variants.some(v => v.options[selectorIndex] === optionValue);
              const variantAvailable = this.data.variants.some(v => v.options[selectorIndex] === optionValue && v.available);
              VariantPicker.updateOptionAvailability(optionEl, variantExists, variantAvailable);
            });
          });
        } else {
          // Flag all options as unavailable
          this.querySelectorAll('.js-option').forEach((optionEl) => {
            VariantPicker.updateOptionAvailability(optionEl, false, false);
          });

          // Flag selector options as available or sold out, depending on the variant availability
          this.optionSelectors.forEach((selector, selectorIndex) => {
            this.data.variants.forEach((variant) => {
              let matchCount = 0;
              let expectedMatches = 0;

              variant.options.forEach((option, optionIndex) => {
                // Skip null values and the current selector index
                if (currVariant.options[optionIndex] !== null && optionIndex !== selectorIndex) {
                  expectedMatches += 1;
                  if (option === currVariant.options[optionIndex]) {
                    matchCount += 1;
                  }
                }
              });

              if (matchCount === expectedMatches) {
                const options = selector.querySelectorAll('.js-option');
                const optionEl = Array.from(options).find((opt) => {
                  if (selector.dataset.selectorType === 'dropdown') {
                    return opt.dataset.value === variant.options[selectorIndex];
                  }
                  return opt.value === variant.options[selectorIndex];
                });

                if (optionEl) {
                  VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
                }
              }
            });
          });
        }
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      if (!evt || !evt.target || typeof evt.target.closest !== 'function') return;
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling && evt.target.nextElementSibling.querySelector('.js-value');
      if (!valueEl) return;
      colorText.textContent = valueEl.textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        if (typeof this.pickUpAvailability.getAvailability === 'function') {
          this.pickUpAvailability.getAvailability(this.variant.id);
        } else {
          customElements.whenDefined('pickup-availability').then(() => {
            if (typeof this.pickUpAvailability.getAvailability === 'function') {
              this.pickUpAvailability.getAvailability(this.variant.id);
            }
          });
        }
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.section.querySelector('.product-info__price .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__default');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        if (!priceCurrentEl) return;

        // Update current price and original price if on sale.
        // Discountly App Code Do not delete
        if( pdMetafields != undefined  && typeof globalFields != 'undefined' ){
          var discountAmount = function(vOrice,tierObj,qty) {
       let originalChangeQty = qty;
            if (isNaN(originalChangeQty)) {
                originalChangeQty = 1;
            }
        var  originalPrice_ = Number(vOrice /100);
        for (var i = 0; i < tierObj.tier_min.length; i++) {
        var tierPrice = parseFloat(tierObj.tier_values[i]);
        var minTier = parseInt(tierObj.tier_min[i]),
        maxTier = tierObj.tier_max[i] != 'max' ? parseInt(tierObj.tier_max[i]) : tierObj.tier_max[i];
        var updatedLinePrice = 0,
        condition1 = false,
        condition2 = false;
        condition1 = originalChangeQty >= minTier;
        condition2 = maxTier == "max" || originalChangeQty <= maxTier;
        if (condition1 && condition2) {
        break;
        }
        }

      var updatedItemPrice = 0;
      if (condition1 && condition2) {
  if (tierObj.discount_type == 'percentage') {
      var originalPriceCut = parseFloat(globalFields.ConvertToFixedDecimalNumber((parseFloat(tierPrice) / 100) * globalFields.ConvertToFixedDecimalNumber(originalPrice_)));
      updatedItemPrice = globalFields.ConvertToFixedDecimalNumber(globalFields.ConvertToFixedDecimalNumber(originalPrice_) - originalPriceCut);
  } else if (tierObj.discount_type == 'fixed') {
      updatedItemPrice = globalFields.ConvertToFixedDecimalNumber(globalFields.ConvertToFixedDecimalNumber(originalPrice_) - parseFloat(tierPrice));
  } else if (tierObj.discount_type == 'fixed_price') {
      updatedItemPrice = globalFields.ConvertToFixedDecimalNumber(parseFloat(tierPrice));
  }

let quantity = originalChangeQty;
var discountTierValue = Number(updatedItemPrice);
var devidedDiscountedAmount = discountTierValue;
let discountAmountValue = Number(devidedDiscountedAmount.toFixed(2));
  if((discountAmountValue >= vOrice )){
  quantity = (Number(discountAmountValue)) * Number(quantity);
  quantity = quantity;
  updatedItemPrice = discountAmountValue;
  return  updatedItemPrice * 100;
  } 
else{
  quantity = discountAmountValue * Number(quantity);
  quantity = quantity;
  updatedItemPrice = updatedItemPrice;
   return  updatedItemPrice * 100;
}
}else{
 return vOrice;       
}
      
    }
         
       var specficProduct = undefined;
       var allcustomerProduct = pdMetafields['customerAllProductMetafileds'];
       var pdmetfileds = specficProduct != undefined && specficProduct.productmetfileds.length > 0 ? specficProduct.productmetfileds[0] : null;
       var allPdmetfileds = allcustomerProduct.length > 0 ? allcustomerProduct[0] : null;
       var discountMetafields = null;
       if(pdmetfileds != null){
          var selectedVariant = variant.id;
           var temp = {
             tier:pdmetfileds
           }
          discountMetafields = globalFields.GetTierObject(temp, selectedVariant);
       }else if(allPdmetfileds != null){
         discountMetafields = allPdmetfileds;
       }
       if(discountMetafields != null){
          var getPriceById = (id) => this.data.variants.find(item => item.id === id).price;
          var priceDisocunt =  discountAmount(getPriceById(variant.id),discountMetafields,1);
          var pricrValue = globalFields.formatMoney(priceDisocunt,globalFields.amount);
          priceCurrentEl.innerHTML = `<span><s>${this.data.formatted[variant.id].price}</s> <span>${pricrValue}</span> </span>`;
        }else{
          priceCurrentEl.innerHTML = `<span>${this.data.formatted[variant.id].price}</span>`;
       }
      }
          else{
          priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
          }
        
       // priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }

      const defaultEl = this.price.querySelector('.price__default');
      if (defaultEl) defaultEl.hidden = !this.variant && !this.preSelection;
      //this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const hash = window.location.hash || '';
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}${hash}` : `${this.dataset.url}${hash}`;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');
    
      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        let variantId = this.variant ? this.variant.id : '';
        // Check if window.variantToBundleMap exists and has a matching key
        if (variantId != '' && window.variantToBundleMap && window.variantToBundleMap[variantId]) {
          const bundleVariantId = window.variantToBundleMap[variantId];
          variantId = bundleVariantId;
        }

        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
